/** Shapes the UI works with. They mirror the SPEC §9 API; field names are camelCase here. */

export type Lang = 'en' | 'ur';
export type Role = 'admin' | 'spender' | 'viewer' | 'accountant' | 'reviewer' | 'custom';
/** What a role may do in the owner app (backend app/core/permissions.py). */
export type Perm = 'view' | 'export' | 'review' | 'edit' | 'delete' | 'audit' | 'cash' | 'categories' | 'users' | 'companies' | 'dues' | 'wallet';
export type Status = 'pending' | 'approved' | 'rejected' | 'needs_correction';

export interface Entity {
  id: string;
  name: string;
  colour: string;
  type: 'company' | 'household';
  /** Short location shown under the name, e.g. "135H – Model Town". */
  address: string | null;
  /** Building icon key: office, house, house-chimney, villa (null = by type). */
  icon: string | null;
  /** The person behind it (shown with its name in Dues). */
  ownerName?: string | null;
  /** The workspace (customer) it belongs to. */
  orgId?: string | null;
  orgName?: string | null;
}

export interface Me {
  id: string;
  fullName: string;
  email: string;
  language: Lang;
  isSuperAdmin: boolean;
  /** The app's word for a company, from the Owner role's name (src/lib/terms.ts). */
  placeWord?: string;
  placesWord?: string;
  /** The Owner role's name (the same in every company). */
  ownerRoleName?: string;
  memberships: { entityId: string; role: Role; roleName?: string; permissions?: Perm[] }[];
  entities: Entity[];
  /** Super Admins: what the Super Admin role can do (the same in every company). */
  superPermissions?: Perm[];
  /** Companies where they record their own purchases (My wallet / the phone app). */
  walletEntityIds?: string[];
}

export interface Wallet {
  walletId: string;
  entityId: string;
  /** Whose wallet it is (a person who also uses the owner app here sees every wallet). */
  userId?: string;
  balance: number;
  lowBalanceThreshold: number;
  unacknowledgedCashIn: number;
}

/** A recharge the spender has not yet confirmed with "Received". */
export interface PendingCashIn {
  id: string;
  entityId: string;
  amount: number;
  givenBy: string;
}

export interface Category {
  id: string;
  entityId: string;
  groupName: string;
  name: string;
  /** Urdu tile label for categories owners add (seeded ones are translated in the app). */
  nameUr: string | null;
  /** Icon key, e.g. "veg", "oil" (see src/ui/icons.ts). */
  icon: string;
  sortOrder: number;
}

export interface Favourite {
  id: string;
  entityId: string;
  categoryId: string;
  name: string;
  defaultPrice: number;
}

/** The latest purchase of one item (drives suggestions and the "Last bought" line). */
export interface ItemPurchase {
  itemName: string;
  categoryId: string;
  quantity: number | null;
  unit: string | null;
  amount: number;
  txnDate: string;
  spenderName: string;
}

export type SyncState = 'synced' | 'queued' | 'failed';

export interface Entry {
  id: string;
  entityId: string;
  categoryId: string;
  itemName: string;
  quantity: number | null;
  unit: string | null;
  amount: number;
  txnDate: string;
  status: Status;
  syncState: SyncState;
  /** Owner's note on needs-correction / rejected entries. */
  reviewNote: string | null;
  noReceiptReason: string | null;
  receiptCount: number;
  /** Another company / house paid for it (Dues); the wallet was left alone. */
  paidByEntityId?: string | null;
  /** Receipt photos on the server, oldest first (open with receiptUrl). Absent in demo data. */
  receiptIds?: string[];
  /** The owner ↔ office boy conversation on this entry, oldest first (never overwritten). */
  messages?: EntryMessage[];
}

export type MessageKind = 'needs_fix' | 'rejected' | 'reply';

export interface EntryMessage {
  id: string;
  kind: MessageKind;
  body: string;
  authorName: string | null;
  createdAt: string;
}

/** Office boy's bell: an owner sent an entry back, rejected it, or gave cash. */
export type SpenderNoteKind = 'needs_fix' | 'rejected' | 'cash_in';

export interface SpenderNotification {
  id: string;
  kind: SpenderNoteKind;
  entityId: string;
  entityName: string;
  entityColour: string;
  transactionId: string;
  itemName: string | null;
  categoryName: string | null;
  amount: number;
  status: Status;
  /** The owner who sent the note or gave the cash. */
  fromName: string | null;
  /** The owner's note (Needs fix / Rejected); null for cash. */
  message: string | null;
  createdAt: string;
  read: boolean;
}

export interface SpenderNotificationList {
  items: SpenderNotification[];
  unread: number;
}

export interface GeoStamp {
  lat: number;
  lng: number;
  accuracyM: number;
  locatedAt: string;
}

export interface NewExpense {
  /** Generated on the phone; the server stores an entry once per clientRef (offline resend). */
  clientRef: string;
  entityId: string;
  categoryId: string;
  itemName: string;
  quantity: number | null;
  unit: string | null;
  amount: number;
  txnDate: string;
  noReceiptReason: string | null;
  photos: Blob[];
  location: GeoStamp | null;
  /** Set when re-saving an entry the owner sent back (needs correction). */
  correctsId: string | null;
}

/** Another company / house: who dues are with (owner panel). */
export interface DuesPlace {
  id: string;
  name: string;
  colour: string;
  archived: boolean;
  ownerName?: string | null;
}

export interface SaveResult {
  entry: Entry;
  /** Wallet balance after this entry (computed, never stored). */
  balance: number;
}

export class ApiError extends Error {
  readonly code: string;
  readonly field: string | undefined;
  /** HTTP status (0 when the server was never reached). */
  readonly status: number;
  constructor(code: string, message: string, field?: string, status = 0) {
    super(message);
    this.code = code;
    this.field = field;
    this.status = status;
  }
}

/** Someone here the signed-in spender can hand cash to. */
export interface HandoverPerson {
  walletId: string;
  name: string;
}

/** Cash the signed-in spender handed another spender, out of his own wallet. */
export interface Handover {
  id: string;
  entityId: string;
  toWalletId: string;
  toName: string;
  amount: number;
  txnDate: string;
  note: string | null;
  /** The receiver tapped Received. */
  acknowledged: boolean;
  createdAt: string;
}

export interface NewHandover {
  entityId: string;
  toWalletId: string;
  amount: number;
  note: string;
}

export interface ExpenseApi {
  requestOtp(email: string): Promise<void>;
  verifyOtp(email: string, code: string): Promise<Me>;
  logout(): Promise<void>;
  me(): Promise<Me | null>;
  setLanguage(lang: Lang): Promise<void>;

  wallets(): Promise<Wallet[]>;
  pendingCashIns(): Promise<PendingCashIn[]>;
  acknowledge(cashInId: string, entityId: string): Promise<void>;

  /** Other spenders here, to hand cash to. */
  handoverPeople(entityId: string): Promise<HandoverPerson[]>;
  /** His own hand-overs here, newest first. */
  myHandovers(entityId: string): Promise<Handover[]>;
  /** Hands cash to another spender here, out of his wallet; returns what he has in hand now. */
  handOver(h: NewHandover): Promise<{ handover: Handover; spendable: number }>;
  /** Takes back what he saved moments ago (the Undo after saving). */
  undo(txnId: string, entityId: string): Promise<void>;

  categories(entityId: string): Promise<Category[]>;
  favourites(entityId: string): Promise<Favourite[]>;
  /** Latest purchase per item in a category, newest first (suggestions). */
  recentItems(entityId: string, categoryId: string): Promise<ItemPurchase[]>;
  lastBought(entityId: string, categoryId: string, itemName: string): Promise<ItemPurchase | null>;

  myEntries(): Promise<Entry[]>;
  saveExpense(expense: NewExpense): Promise<SaveResult>;
  deleteEntry(entryId: string, entityId: string): Promise<void>;
  /** Answer the owner's "Needs fix" note; the entry goes back to Pending. */
  replyToFix(entryId: string, entityId: string, message: string): Promise<Entry>;

  /** The office boy's own notifications (bell on Home), newest first. */
  notifications(): Promise<SpenderNotificationList>;
  markNotificationRead(id: string): Promise<void>;
  markAllNotificationsRead(): Promise<void>;
  /** A short-lived link to one of the spender's own receipt photos. */
  receiptUrl(receiptId: string, entityId: string): Promise<string>;
}
